import { useState, useEffect, useMemo } from 'preact/hooks'
import { useLocation } from 'preact-iso'
import { X, ChevronRight, ChevronDown, AlertTriangle } from 'lucide-preact'
import { useLookups } from '@/hooks/useLookups'
import { websApi, type WebCreateDto, type WebViewDto, type OnlineEventImportDto } from '@/api/webs'
import { audienceApi } from '@/api/audience'
import type { CampaignFileImportListDto } from '@/types/audience'
import type { OptionDto } from '@/types/lookups'
import { useToast } from '@/hooks/useToast'
import { TextBoxField, Select, DatePicker, NumberInput, MultiSelect } from '@/components'
import { handleApiError, setFormValidationErrors } from '@/utils/errorHandling'
import { setWebView } from '@/state/websCache'
import { routes } from '@/utils/routes'
import { useAIForm } from '@/hooks/useAIForm'
import type { AIFieldConfig } from '@/hooks/useAIForm'
import { useBigMarkerIntegration } from '@/hooks/useBigMarkerIntegration'
import { getWebTypeMeta } from '@/constants/webTypes'
import { ChatLocations, ChatEntityTypes, ChatViewModes } from '@/constants/aiChat'

/** Create a web from scratch, or import one from an existing BigMarker conference. */
export type NewWebPanelMode = 'create' | 'import'

interface NewWebPanelProps {
    isOpen: boolean
    onClose: () => void
    clientCompanyId: string
    workspaceId: string
    campaignId: string
    /** Shown under the panel title so it is clear which campaign the web lands in. */
    campaignName?: string
    mode?: NewWebPanelMode
    /**
     * `keepOpen` is set by "Save & add another": the web was created, but the panel is staying up for
     * the next one, so the caller should refresh behind it rather than close and navigate.
     */
    onWebCreated: (web: { Id: string; Name: string; Type: string }, options?: { keepOpen?: boolean }) => void
    onRequestNewAudience?: () => void
}

type FormValues = {
    Name: string
    Type: string
    LaunchDate: string
    Description?: string
    LeadGoal?: string
    OnlineEvent_EventType?: string
    OnlineEvent_ModeType?: string
    OnlineEvent_ConferenceId?: string
    CalendlyUrl?: string
}

const NAME_MAX_LENGTH = 200

/**
 * Web types are identified by their lookup description — there is no enum on the client. Normalising
 * away case and spacing means "Online Event" and "OnlineEvent" both resolve, so this does not break
 * the way keying off a raw description string does.
 */
const normalizeTypeLabel = (label: string | undefined) => (label || '').replace(/\s+/g, '').toLowerCase()
const isOnlineEventOption = (option: OptionDto) => normalizeTypeLabel(option.Description) === 'onlineevent'

/**
 * Create a demand web. One screen, no steps.
 *
 * Title, timezone and the schedule are deliberately absent: campaign managers plan 90 days out and
 * create webs before the content call has decided any of them, so those are collected later and
 * enforced at sign-off (ORB-459 / ORB-390). The slug is not collected at all — it is minted from the
 * signed-off title. What is left is a reference name, a type, and the date marketing starts.
 */
export function NewWebPanel({
    isOpen, onClose, clientCompanyId, workspaceId, campaignId, campaignName,
    mode = 'create', onWebCreated, onRequestNewAudience,
}: NewWebPanelProps) {
    const { getWebTypes, getOnlineEventTypes, getOnlineModeTypes } = useLookups()
    const { showError, showSuccess } = useToast()
    const location = useLocation()
    // Only the error state matters here — whether BigMarker is connected at all is what decides if the
    // menu offers the import entry point in the first place.
    const { integrationError } = useBigMarkerIntegration(clientCompanyId)

    const [isSubmitting, setIsSubmitting] = useState(false)
    const [showOptional, setShowOptional] = useState(false)
    const [audiences, setAudiences] = useState<CampaignFileImportListDto[]>([])
    const [audiencesLoading, setAudiencesLoading] = useState(false)
    const [audiencesError, setAudiencesError] = useState<string | null>(null)
    const [selectedAudienceIds, setSelectedAudienceIds] = useState<string[]>([])

    const isImport = mode === 'import'
    const webTypeOptions = getWebTypes()
    const onlineEventTypeKey = webTypeOptions.find(isOnlineEventOption)?.Key

    const aiFields = useMemo<AIFieldConfig[]>(() => [
        { aiName: 'webName', formPath: 'Name', displayName: 'Reference Name', fieldType: 'text', maxLength: NAME_MAX_LENGTH },
        { aiName: 'webDescription', formPath: 'Description', displayName: 'Description', fieldType: 'textarea', maxLength: 5000 },
        { aiName: 'webType', formPath: 'Type', displayName: 'Web Type', fieldType: 'select' },
    ], [])

    const { form } = useAIForm<FormValues>({
        mode: 'onChange',
        // LeadGoal defaults to '0' rather than blank: 0 is what the server stores for "no goal", so
        // showing it is the honest starting state. A blank field implied an "unset" the API cannot hold.
        defaultValues: { Name: '', Type: '', LaunchDate: '', Description: '', LeadGoal: '0', CalendlyUrl: '' },
    }, {
        aiContext: {
            campaignId,
            location: ChatLocations.WebEditor,
            entityType: ChatEntityTypes.Web,
            viewMode: ChatViewModes.Create,
        },
        fields: aiFields,
        contextSuppressed: !isOpen,
        isEditMode: false,
    })

    const { register, control, formState, watch, reset, setError, getValues, setValue } = form
    const { errors, isValid } = formState

    const nameValue = watch('Name')
    const launchDateValue = watch('LaunchDate')
    const selectedType = watch('Type')
    const eventTypeValue = watch('OnlineEvent_EventType')
    const modeTypeValue = watch('OnlineEvent_ModeType')
    const conferenceIdValue = watch('OnlineEvent_ConferenceId')

    // An imported conference is always an online event, so the type is not asked for in import mode.
    const effectiveType = isImport ? (onlineEventTypeKey || '') : selectedType
    const selectedTypeLabel = webTypeOptions.find(option => option.Key === effectiveType)?.Description
    const normalizedType = normalizeTypeLabel(selectedTypeLabel)
    const isOnlineEvent = normalizedType === 'onlineevent'
    const isDownload = normalizedType === 'download'
    const isMeeting = normalizedType === 'meeting'
    const isDemo = normalizedType === 'demo'
    const usesCalendly = isMeeting || isDemo

    useEffect(() => {
        if (!isOpen) return
        const load = async () => {
            setAudiencesLoading(true)
            setAudiencesError(null)
            const res = await audienceApi.listByCampaign(clientCompanyId, workspaceId, campaignId)
            if (res.data) setAudiences(res.data)
            else setAudiencesError(res.error || 'Failed to load audiences')
            setAudiencesLoading(false)
        }
        load()
    }, [isOpen, clientCompanyId, workspaceId, campaignId])

    // `isValid` is what stops an over-long name reaching the API and coming back as a 422 — the previous
    // step gate checked only that the fields were non-empty and ignored react-hook-form entirely.
    const hasRequiredFields = isImport
        ? !!conferenceIdValue?.trim() && !!nameValue?.trim() && !!launchDateValue && !!onlineEventTypeKey
        : !!nameValue?.trim() && !!effectiveType && !!launchDateValue
            && (!isOnlineEvent || (!!eventTypeValue && !!modeTypeValue))
    const canSubmit = hasRequiredFields && isValid && !isSubmitting && !(isImport && integrationError)

    const resetPanel = (keepPlanningContext: boolean) => {
        const current = getValues()
        reset({
            Name: '',
            // A planning session creates several webs of the same type on the same marketing date, which
            // is the entire reason "Save & add another" exists — so those two carry over.
            Type: keepPlanningContext ? current.Type : '',
            LaunchDate: keepPlanningContext ? current.LaunchDate : '',
            Description: '',
            LeadGoal: '0',
            CalendlyUrl: '',
            OnlineEvent_EventType: keepPlanningContext ? current.OnlineEvent_EventType : undefined,
            OnlineEvent_ModeType: keepPlanningContext ? current.OnlineEvent_ModeType : undefined,
            OnlineEvent_ConferenceId: '',
        })
        // Audiences always clear, including on "Save & add another". Only the type and the marketing
        // date carry forward — audiences decide who actually gets emailed, so inheriting them silently
        // is a different order of mistake from inheriting a date you can see in the field above.
        setSelectedAudienceIds([])
        if (!keepPlanningContext) {
            setShowOptional(false)
        }
    }

    const handleClose = () => {
        resetPanel(false)
        onClose()
    }

    // Close on Escape, matching the app's panel convention, but never mid-save.
    useEffect(() => {
        if (!isOpen) return
        const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape' && !isSubmitting) handleClose() }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [isOpen, isSubmitting])

    const buildPayload = (values: FormValues): WebCreateDto => ({
        Name: values.Name.trim(),
        Type: effectiveType,
        LaunchDate: values.LaunchDate,
        Description: values.Description?.trim() || undefined,
        // Always a number. 0 is the stored value for "no goal" — the field defaults to it and the
        // fallback only covers a user clearing the box before submitting.
        LeadGoal: values.LeadGoal && values.LeadGoal.trim() !== '' ? Number(values.LeadGoal) : 0,
        IncludedFileImportIds: selectedAudienceIds.length ? selectedAudienceIds : undefined,
        // No Title on any of these. Name is an internal planning code and must never become the public
        // title — the sub-entity is created empty and titled at sign-off.
        OnlineEvent: isOnlineEvent
            ? (isImport
                ? ({ ConferenceId: values.OnlineEvent_ConferenceId!.trim() } satisfies OnlineEventImportDto)
                : { EventType: values.OnlineEvent_EventType!, ModeType: values.OnlineEvent_ModeType! })
            : undefined,
        Download: isDownload ? {} : undefined,
        Meeting: isMeeting ? { CalendlyUrl: values.CalendlyUrl?.trim() || undefined } : undefined,
        Demo: isDemo ? { CalendlyUrl: values.CalendlyUrl?.trim() || undefined } : undefined,
    })

    const submit = async (addAnother: boolean) => {
        if (!canSubmit) return
        setIsSubmitting(true)
        try {
            const resp = await websApi.createWeb(clientCompanyId, workspaceId, campaignId, buildPayload(getValues()))
            if (resp.error || !resp.data || resp.status >= 400) {
                if (resp.status === 422) setFormValidationErrors(resp, setError)
                else handleApiError(resp, showError, () => { }, isImport ? 'Import Failed' : 'Create Web Failed')
                return
            }

            const created = resp.data
            showSuccess(
                isImport ? 'Conference imported' : 'Web created',
                isImport
                    ? `${created.Name} was created, and the conference was imported successfully.`
                    : `${created.Name} was created successfully.`,
            )

            if (addAnother) {
                onWebCreated({ Id: created.Id, Name: created.Name, Type: created.Type }, { keepOpen: true })
                resetPanel(true)
                return
            }

            onWebCreated({ Id: created.Id, Name: created.Name, Type: created.Type })
            setWebView(created as WebViewDto)
            const scopedRoutes = routes.scoped(clientCompanyId, workspaceId)
            location.route(scopedRoutes.web(campaignId, created.Id))
            handleClose()
        } catch (error) {
            handleApiError(error, showError, () => { }, isImport ? 'Import Failed' : 'Create Web Failed')
        } finally {
            setIsSubmitting(false)
        }
    }

    if (!isOpen) return null

    const referenceNameField = (
        <TextBoxField
            control={control}
            name="Name"
            labelSize="xs"
            label="Reference name"
            required
            autoFocus
            showCharCounter
            maxLength={NAME_MAX_LENGTH}
            placeholder="Q3-LM1-Aug"
            className="font-mono"
            helperText={isImport
                ? 'Internal only. Title, schedule and timezone come across from BigMarker.'
                : 'Internal only, for finding this web and tagging leads in the CRM. The public title is set later, at sign-off.'}
            rules={{
                required: 'Reference name is required',
                maxLength: { value: NAME_MAX_LENGTH, message: `Max ${NAME_MAX_LENGTH} characters` },
                validate: value => (!!value?.trim()) || 'Reference name cannot be empty',
            }}
        />
    )

    const marketingStartsField = (
        <DatePicker
            labelSize="xs"
            label="Marketing starts"
            required
            error={errors.LaunchDate?.message}
            helperText="Target date the first email goes out. Milestones count backwards from here."
            {...register('LaunchDate', { required: 'Marketing start date is required' })}
        />
    )

    return (
        <div class="fixed inset-0 z-50 flex" aria-modal="true" role="dialog">
            <div class="flex-1 bg-slate-900/40" onClick={handleClose} />
            <div class="w-full sm:w-[480px] lg:w-[540px] ml-auto h-full border-l border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xl flex flex-col">
                {/* Header */}
                <div class="flex items-start justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                    <div>
                        <h3 class="text-lg font-semibold text-slate-900 dark:text-white">
                            {isImport ? 'Import from BigMarker' : 'New demand web'}
                        </h3>
                        {campaignName && (
                            <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{campaignName}</p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={handleClose}
                        class="inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        aria-label="Close panel"
                    >
                        <X class="w-4 h-4" />
                    </button>
                </div>

                {/* Body */}
                <div class="flex-1 overflow-y-auto px-6 py-5">
                    {isImport ? (
                        <div class="space-y-5">
                            {integrationError && (
                                <div class="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
                                    <AlertTriangle class="w-4 h-4 mt-0.5 shrink-0" />
                                    <span>BigMarker Connection Error — the integration is in an error state. Importing by Conference ID is unavailable until the connection is restored.</span>
                                </div>
                            )}
                            <TextBoxField
                                control={control}
                                name="OnlineEvent_ConferenceId"
                                labelSize="xs"
                                label="Conference ID"
                                required
                                autoFocus
                                showCharCounter
                                maxLength={100}
                                placeholder="8f3c21a904"
                                className="font-mono"
                                helperText="From the conference URL in BigMarker."
                                rules={{
                                    required: 'Conference ID is required',
                                    maxLength: { value: 100, message: 'Max 100 characters' },
                                    validate: value => (!!value?.trim()) || 'Conference ID cannot be empty',
                                }}
                            />
                            {referenceNameField}
                            {/* BigMarker knows when the event runs, but not when marketing for it starts. */}
                            {marketingStartsField}
                        </div>
                    ) : (
                        <div class="space-y-5">
                            {referenceNameField}

                            <div>
                                <label class="block text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
                                    Type <span class="text-red-500">*</span>
                                </label>
                                <div class="grid grid-cols-2 gap-2">
                                    {webTypeOptions.map(option => {
                                        const meta = getWebTypeMeta(option.Description)
                                        const TypeIcon = meta.Icon
                                        const selected = effectiveType === option.Key
                                        return (
                                            <button
                                                key={option.Key}
                                                type="button"
                                                onClick={() => setValue('Type', option.Key, { shouldValidate: true, shouldDirty: true })}
                                                aria-pressed={selected}
                                                class={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors text-left ${selected
                                                    ? 'border-primary-600 ring-1 ring-primary-600 text-slate-900 dark:text-white bg-primary-50 dark:bg-primary-900/20'
                                                    : 'border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700'}`}
                                            >
                                                <span class={`inline-flex items-center justify-center w-6 h-6 rounded-full shrink-0 ${meta.chipClass}`}>
                                                    <TypeIcon class="w-3.5 h-3.5" />
                                                </span>
                                                {option.Description}
                                            </button>
                                        )
                                    })}
                                </div>
                                {/* Registered so the form tracks the type for validation. */}
                                <input type="hidden" {...register('Type', { required: 'Type is required' })} />
                                {errors.Type && <p class="text-xs text-red-600 dark:text-red-400 mt-1.5">{errors.Type.message}</p>}
                            </div>

                            {/* Choosing an online event adds the two fields the rest of that screen keys off.
                                Everything else about the event is decided later. */}
                            {isOnlineEvent && (
                                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <Select
                                        labelSize="xs"
                                        label="Event type"
                                        required
                                        error={errors.OnlineEvent_EventType?.message as string}
                                        options={getOnlineEventTypes().map(option => ({ value: option.Key, label: option.Description }))}
                                        placeholder="Select an event type"
                                        {...register('OnlineEvent_EventType', { required: isOnlineEvent ? 'Event type is required' : false })}
                                    />
                                    <Select
                                        labelSize="xs"
                                        label="Mode"
                                        required
                                        error={errors.OnlineEvent_ModeType?.message as string}
                                        options={getOnlineModeTypes().map(option => ({ value: option.Key, label: option.Description }))}
                                        placeholder="Select a mode"
                                        {...register('OnlineEvent_ModeType', { required: isOnlineEvent ? 'Mode is required' : false })}
                                    />
                                </div>
                            )}

                            {marketingStartsField}

                            <div class="border-t border-slate-200 dark:border-slate-700 pt-3">
                                <button
                                    type="button"
                                    onClick={() => setShowOptional(open => !open)}
                                    aria-expanded={showOptional}
                                    class="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white"
                                >
                                    {showOptional ? <ChevronDown class="w-4 h-4" /> : <ChevronRight class="w-4 h-4" />}
                                    Description, audiences, lead goal{usesCalendly ? ', booking link' : ''}
                                </button>

                                {showOptional && (
                                    <div class="space-y-4 mt-4">
                                        <TextBoxField
                                            control={control}
                                            name="Description"
                                            labelSize="xs"
                                            label="Description"
                                            multiline
                                            rows={3}
                                            showCharCounter
                                            maxLength={5000}
                                            placeholder="Optional summary"
                                            rules={{ maxLength: { value: 5000, message: 'Max 5000 characters' } }}
                                        />
                                        <MultiSelect
                                            label="Audiences"
                                            options={audiences.map(audience => ({ value: audience.Id, label: audience.Name || 'Unnamed' }))}
                                            value={selectedAudienceIds}
                                            onChange={(selected) => setSelectedAudienceIds(selected as string[])}
                                            isLoading={audiencesLoading}
                                            error={audiencesError || undefined}
                                            emptyMessage="No audiences yet."
                                            emptyAction={onRequestNewAudience ? { label: 'Create one', onClick: onRequestNewAudience } : undefined}
                                            helperText="Optional now; required before the web can start marketing."
                                        />
                                        <NumberInput
                                            labelSize="xs"
                                            label="Lead goal"
                                            min={0}
                                            step={1}
                                            error={errors.LeadGoal?.message as string}
                                            placeholder="0"
                                            helperText="0 means no goal is being tracked."
                                            {...register('LeadGoal', {
                                                validate: (value) => {
                                                    if (value === undefined || value === null || value === '') return true
                                                    const parsed = Number(value)
                                                    if (Number.isNaN(parsed)) return 'Must be a number'
                                                    if (!Number.isInteger(parsed)) return 'Must be an integer'
                                                    if (parsed < 0) return 'Must be >= 0'
                                                    if (parsed > 100000000) return 'Too large'
                                                    return true
                                                },
                                            })}
                                        />
                                        {/* The API has accepted a CalendlyUrl on Meeting and Demo since they were
                                            added, but no create screen ever offered one. */}
                                        {usesCalendly && (
                                            <TextBoxField
                                                control={control}
                                                name="CalendlyUrl"
                                                labelSize="xs"
                                                label="Booking link"
                                                type="url"
                                                maxLength={500}
                                                placeholder="https://calendly.com/your-team/30min"
                                                helperText="Where leads book time. Can be added later."
                                                rules={{
                                                    maxLength: { value: 500, message: 'Max 500 characters' },
                                                    validate: (value) => {
                                                        const trimmed = (value || '').trim()
                                                        if (!trimmed) return true
                                                        return /^https?:\/\/.+/i.test(trimmed) || 'Enter a full URL starting with https://'
                                                    },
                                                }}
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/*
                  * Footer actions cluster LEFT, primary first, rather than the usual
                  * justify-between split: this panel sits flush right and the Webster launcher is
                  * fixed at bottom-6 right-6 with z-[60] against the drawer's z-50, so a
                  * right-aligned primary button lands under it and can't be clicked. Other
                  * drawers suppress Webster instead (aiChatSuppressed); here we keep him
                  * reachable and move the buttons. ORB-470
                  */}
                <div class="flex items-center gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700">
                    {isImport ? (
                        <>
                            <button
                                type="button"
                                disabled={!canSubmit}
                                onClick={() => submit(false)}
                                class="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                            >
                                {isSubmitting && <div class="w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin" />}
                                {isSubmitting ? 'Importing…' : 'Import'}
                            </button>
                            <button
                                type="button"
                                onClick={handleClose}
                                class="px-3 py-2 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                            >Cancel</button>
                        </>
                    ) : (
                        <>
                            <button
                                type="button"
                                disabled={!canSubmit}
                                onClick={() => submit(false)}
                                class="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                            >
                                {isSubmitting && <div class="w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin" />}
                                {isSubmitting ? 'Creating…' : 'Create'}
                            </button>
                            <button
                                type="button"
                                disabled={!canSubmit}
                                onClick={() => submit(true)}
                                class="px-3 py-2 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
                            >Save &amp; add another</button>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}
